import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import configService from '../services/config';
import './Leave.css';
import {
  Modal,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyboardIcon from '@mui/icons-material/Keyboard';

// लाइट थीम के लिए सॉफ्ट और प्रोफेशनल स्टेटस कलर्स
const STATUS_COLORS = {
  Pending: { bg: '#FFF7ED', color: '#EA580C', border: '#FED7AA' },
  Approved: { bg: '#F0FDF4', color: '#16A34A', border: '#BBF7D0' },
  Rejected: { bg: '#FEF2F2', color: '#DC2626', border: '#FCA5A5' },
};

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

// Date picker formatters
const getTodayString = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getNextWeekString = () => {
  const futureDate = new Date();
  futureDate.setDate(futureDate.getDate() + 6);
  const yyyy = futureDate.getFullYear();
  const mm = String(futureDate.getMonth() + 1).padStart(2, '0');
  const dd = String(futureDate.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// 12-hour to 24-hour converter
const convertTo24Hour = (timeStr) => {
  if (!timeStr || timeStr === '—') return '00:00:00';
  const [time, modifier] = timeStr.split(' ');
  let [hours, minutes] = time.split(':');

  if (hours === '12') {
    hours = '00';
  }
  if (modifier === 'PM') {
    hours = parseInt(hours, 10) + 12;
  }

  return `${String(hours).padStart(2, '0')}:${minutes}:00`;
};

export default function AttendanceRegularizationList() {
  const { company_id, userId } = useSelector(state => state.globalVariables);
  const [regularizations, setRegularizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // --- MUI Modal Form States ---
  const [openModal, setOpenModal] = useState(false);
  const [category, setCategory] = useState('');
  const [timeType, setTimeType] = useState('');
  const [selectedDate, setSelectedDate] = useState(getTodayString());
  const [beginningTime, setBeginningTime] = useState('12:00 AM');
  const [endTime, setEndTime] = useState('12:00 AM');

  // --- Clock Picker States ---
  const [openPicker, setOpenPicker] = useState(false);
  const [activeField, setActiveField] = useState('beginning');
  const [pickerView, setPickerView] = useState('hours');
  const [selectedHour, setSelectedHour] = useState('12');
  const [selectedMinute, setSelectedMinute] = useState('00');
  const [ampm, setAmpm] = useState('AM');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [regRes] = await Promise.all([
        configService.getRegularizationList({ company_id: company_id, employee_id: userId }),
      ]);

      if (regRes?.status) {
        setRegularizations(regRes.data || []);
      } else {
        toast.error(regRes?.message || 'No regularization data found');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load data. Check network or backend.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [company_id, userId]);

  useEffect(() => {
    setPage(1);
  }, [rowsPerPage, regularizations.length]);

  // --- Submission Logic ---
  const handleSave = async () => {
    if (!category || !timeType || !selectedDate) {
      toast.error("Please fill all the required fields (Category, Time Type, and Date).");
      return;
    }

    const formattedBeginning = convertTo24Hour(beginningTime);
    const formattedEnd = convertTo24Hour(endTime);

    const formData = {
      company_id: company_id,
      employee_id: userId,
      customer_id: userId,
      category: category,
      time_type: timeType,
      entry_date: selectedDate,
      beginning: formattedBeginning,
      end_time: formattedEnd,
      remarks: timeType,
    };

    try {
      setLoading(true);
      const res = await configService.attendanceregularization(formData);

      if (res?.status === true || res?.data?.status === true || res?.status === 201) {
        toast.success("Attendance Regularization saved successfully!");
        handleCloseModal();
        await fetchData(); // Refresh table data
      } else {
        toast.error(res?.data?.message || res?.message || "Failed to save.");
      }
    } catch (error) {
      console.error("Error saving attendance regularization:", error);
      toast.error(`Error: ${error?.response?.data?.message || error?.message || "Server Error"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCategory('');
    setTimeType('');
    setBeginningTime('12:00 AM');
    setEndTime('12:00 AM');
    setSelectedDate(getTodayString());
  };

  // --- Clock Picker Logic ---
  const handleOpenPicker = (field, currentTime) => {
    setActiveField(field);
    try {
      const [time, period] = currentTime.split(' ');
      const [hr, min] = time.split(':');
      setSelectedHour(hr || '12');
      setSelectedMinute(min || '00');
      setAmpm(period || 'AM');
    } catch (e) {
      setSelectedHour('12');
      setSelectedMinute('00');
      setAmpm('AM');
    }
    setPickerView('hours');
    setOpenPicker(true);
  };

  const handleClockSave = () => {
    const formattedTime = `${selectedHour}:${selectedMinute} ${ampm}`;
    if (activeField === 'beginning') {
      setBeginningTime(formattedTime);
    } else {
      setEndTime(formattedTime);
    }
    setOpenPicker(false);
  };

  const hoursData = [
    { n: '12', deg: 0 }, { n: '01', deg: 30 }, { n: '02', deg: 60 },
    { n: '03', deg: 90 }, { n: '04', deg: 120 }, { n: '05', deg: 150 },
    { n: '06', deg: 180 }, { n: '07', deg: 210 }, { n: '08', deg: 240 },
    { n: '09', deg: 270 }, { n: '10', deg: 300 }, { n: '11', deg: 330 }
  ];

  const minutesData = [
    { n: '00', deg: 0 }, { n: '05', deg: 30 }, { n: '10', deg: 60 },
    { n: '15', deg: 90 }, { n: '20', deg: 120 }, { n: '25', deg: 150 },
    { n: '30', deg: 180 }, { n: '35', deg: 210 }, { n: '40', deg: 240 },
    { n: '45', deg: 270 }, { n: '50', deg: 300 }, { n: '55', deg: 330 }
  ];

  const currentData = pickerView === 'hours' ? hoursData : minutesData;
  const currentSelection = pickerView === 'hours' ? selectedHour : selectedMinute;
  const currentRotation = currentData.find(d => d.n === currentSelection)?.deg || 0;

  // --- Table Helpers ---
  const getCustomerName = (id) => {
    const c = customers.find(c => String(c.id) === String(id));
    return c?.fname || id;
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  const totalRows = regularizations.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return regularizations.slice(start, start + rowsPerPage);
  }, [regularizations, page, rowsPerPage]);

  const rangeStart = totalRows === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const rangeEnd = Math.min(page * rowsPerPage, totalRows);

  const goPrev = () => setPage(p => Math.max(1, p - 1));
  const goNext = () => setPage(p => Math.min(totalPages, p + 1));

  if (loading && regularizations.length === 0) {
    return (
      <div className="leave-root" style={{ textAlign: 'center', paddingTop: 40 }}>
        <div className="loader" style={{ color: '#4f8ef7' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="leave-root fade-in-up" style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>
      {/* --- HEADER WITH BUTTON --- */}
      <div className="leave-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 className="leave-title" style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#1e293b' }}>Attendance Regularization</h2>
        </div>
        <Button 
          variant="contained" 
          onClick={() => setOpenModal(true)}
          sx={{
            bgcolor: '#6c5ce7',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 2,
            boxShadow: '0 4px 6px -1px rgba(108, 92, 231, 0.2)',
            '&:hover': { bgcolor: '#5b4bc4' }
          }}
        >
          Apply Regularization
        </Button>
      </div>

      {/* --- DATA TABLE --- */}
      <div
        className="leave-list page-card delay-1 fade-in-up"
        style={{
          background: '#ffffff',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px -1px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
        }}
      >
        {totalRows === 0 ? (
          <div className="leave-empty" style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
            <p>No regularization requests found.</p>
          </div>
        ) : (
          <>
            <div className="table-responsive" style={{ overflowX: 'auto' }}>
              <table
                className="custom-table"
                style={{ width: '100%', borderCollapse: 'collapse', minWidth: '650px' }}
              >
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    <th style={{ padding: '16px 20px', textAlign: 'left', color: '#475569', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', color: '#475569', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Time Type</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', color: '#475569', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Entry Date</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', color: '#475569', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Remarks</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', color: '#475569', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((r, idx) => {
                    const status = r.status || 'Pending';
                    const sc = STATUS_COLORS[status] || STATUS_COLORS.Pending;
                    const isLast = idx === paginatedRows.length - 1;
                    return (
                      <tr
                        key={r.id}
                        style={{
                          borderBottom: isLast ? 'none' : '1px solid #e2e8f0',
                          backgroundColor: '#ffffff',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                      >
                        <td style={{ padding: '16px 20px', color: '#334155', fontWeight: 500 }}>{r.category}</td>
                        <td style={{ padding: '16px 20px', color: '#334155' }}>{r.time_type}</td>
                        <td style={{ padding: '16px 20px', color: '#64748b' }}>{fmtDate(r.entry_date)}</td>
                        <td style={{ padding: '16px 20px', color: '#64748b', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.remarks}</td>
                        <td style={{ padding: '16px 20px' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 600,
                              background: sc.bg,
                              color: sc.color,
                              border: `1px solid ${sc.border}`,
                            }}
                          >
                            {status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 24,
                padding: '14px 20px',
                borderTop: '1px solid #e2e8f0',
                background: '#ffffff',
                flexWrap: 'wrap'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 14 }}>
                <span>Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  style={{
                    background: '#fff',
                    color: '#334155',
                    border: '1px solid #cbd5e1',
                    borderRadius: 6,
                    padding: '4px 8px',
                    fontSize: 14,
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                >
                  {ROWS_PER_PAGE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div style={{ color: '#64748b', fontSize: 14 }}>
                {rangeStart}–{rangeEnd} of {totalRows}
              </div>

              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  onClick={goPrev}
                  disabled={page === 1}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: page === 1 ? '#cbd5e1' : '#334155',
                    fontSize: 18,
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    padding: '4px 8px',
                  }}
                >
                  ‹
                </button>
                <button
                  onClick={goNext}
                  disabled={page === totalPages}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: page === totalPages ? '#cbd5e1' : '#334155',
                    fontSize: 18,
                    cursor: page === totalPages ? 'not-allowed' : 'pointer',
                    padding: '4px 8px',
                  }}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Popup MUI Form Modal ── */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, zIndex: 1000 }}
      >
        <Card sx={{
          width: '100%',
          maxWidth: 500,
          bgcolor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 4,
          color: '#1e293b',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
              New Regularization Request
            </Typography>

            <Grid container spacing={3}>
              {/* 1. Category */}
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="cat-label" sx={{ color: '#64748b', '&.Mui-focused': { color: '#6c5ce7' } }}>Category</InputLabel>
                  <Select
                    labelId="cat-label"
                    value={category}
                    label="Category"
                    onChange={(e) => setCategory(e.target.value)}
                    sx={{
                      color: '#1e293b',
                      '.MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#94a3b8' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6c5ce7' },
                      '.MuiSvgIcon-root': { color: '#64748b' }
                    }}
                    MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff', color: '#1e293b', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' } } }}
                  >
                    <MenuItem value="Working Times">Working Times</MenuItem>
                    <MenuItem value="Break">Break</MenuItem>
                    <MenuItem value="Overtime">Overtime</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* 2. Time Type */}
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="type-label" sx={{ color: '#64748b', '&.Mui-focused': { color: '#6c5ce7' } }}>Time Type</InputLabel>
                  <Select
                    labelId="type-label"
                    value={timeType}
                    label="Time Type"
                    onChange={(e) => setTimeType(e.target.value)}
                    sx={{
                      color: '#1e293b',
                      '.MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#94a3b8' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6c5ce7' },
                      '.MuiSvgIcon-root': { color: '#64748b' }
                    }}
                    MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff', color: '#1e293b', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' } } }}
                  >
                    <MenuItem value="Attendance Regularization">Attendance Regularization</MenuItem>
                    <MenuItem value="On Duty">On Duty</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* 3. Date Picker */}
              <Grid item xs={12}>
                <TextField
                  label="Date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{
                    min: getTodayString(),
                    max: getNextWeekString()
                  }}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarTodayIcon sx={{ color: '#6c5ce7', fontSize: '1.2rem' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': { color: '#64748b' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#6c5ce7' },
                    '& .MuiOutlinedInput-root': {
                      color: '#1e293b',
                      '& fieldset': { borderColor: '#cbd5e1' },
                      '&:hover fieldset': { borderColor: '#94a3b8' },
                      '&.Mui-focused fieldset': { borderColor: '#6c5ce7' },
                    }
                  }}
                />
              </Grid>

              {/* 4. Beginning Time */}
              <Grid item xs={12}>
                <TextField
                  label="Beginning"
                  value={beginningTime}
                  onClick={() => handleOpenPicker('beginning', beginningTime)}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <AccessTimeIcon sx={{ color: '#6c5ce7', cursor: 'pointer' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    cursor: 'pointer',
                    '& .MuiInputLabel-root': { color: '#64748b' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#6c5ce7' },
                    '& .MuiOutlinedInput-root': {
                      color: '#1e293b',
                      cursor: 'pointer',
                      '& fieldset': { borderColor: '#cbd5e1' },
                      '&:hover fieldset': { borderColor: '#94a3b8' },
                      '&.Mui-focused fieldset': { borderColor: '#6c5ce7' },
                    },
                    '& input': { cursor: 'pointer' }
                  }}
                />
              </Grid>

              {/* 5. End Time */}
              <Grid item xs={12}>
                <TextField
                  label="End"
                  value={endTime}
                  onClick={() => handleOpenPicker('end', endTime)}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <AccessTimeIcon sx={{ color: '#6c5ce7', cursor: 'pointer' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    cursor: 'pointer',
                    '& .MuiInputLabel-root': { color: '#64748b' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#6c5ce7' },
                    '& .MuiOutlinedInput-root': {
                      color: '#1e293b',
                      cursor: 'pointer',
                      '& fieldset': { borderColor: '#cbd5e1' },
                      '&:hover fieldset': { borderColor: '#94a3b8' },
                      '&.Mui-focused fieldset': { borderColor: '#6c5ce7' },
                    },
                    '& input': { cursor: 'pointer' }
                  }}
                />
              </Grid>

              {/* 6. Created By */}
              <Grid item xs={12}>
                <TextField
                  label="Created By"
                  value={userId || 'User'}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountCircleIcon sx={{ color: '#94a3b8', mr: 0.5 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': { color: '#64748b' },
                    '& .MuiOutlinedInput-root': {
                      color: '#64748b',
                      bgcolor: '#f8fafc',
                      '& fieldset': { borderColor: '#cbd5e1' },
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* Modal Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
              <Button onClick={handleCloseModal} sx={{ color: '#64748b', '&:hover': { color: '#1e293b', bgcolor: '#f1f5f9' }, textTransform: 'none' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{ bgcolor: '#6c5ce7', color: '#fff', px: 3, textTransform: 'none', '&:hover': { bgcolor: '#5b4bc4' } }}
              >
                Save
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Modal>

      {/* ── CUSTOM RADIAL CLOCK DIALOG PICKER ── */}
      <Dialog
        open={openPicker}
        onClose={() => setOpenPicker(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 3,
            maxWidth: '340px',
            width: '100%',
            bgcolor: '#ffffff',
            border: '1px solid #e2e8f0',
            color: '#1e293b',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }
        }}
        sx={{ zIndex: 1100 }}
      >
        <DialogContent sx={{ p: 0, overflow: 'visible' }}>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2, fontWeight: 500 }}>
            Select time
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
            <Box
              onClick={() => setPickerView('hours')}
              sx={{
                bgcolor: pickerView === 'hours' ? 'rgba(108, 92, 231, 0.1)' : '#f8fafc',
                px: 2.5, py: 1.5, borderRadius: 2, cursor: 'pointer',
                border: pickerView === 'hours' ? '1px solid #6c5ce7' : '1px solid #cbd5e1'
              }}
            >
              <Typography variant="h3" sx={{ color: pickerView === 'hours' ? '#6c5ce7' : '#1e293b', fontWeight: 500 }}>
                {selectedHour}
              </Typography>
            </Box>

            <Typography variant="h4" sx={{ color: '#64748b' }}>:</Typography>

            <Box
              onClick={() => setPickerView('minutes')}
              sx={{
                bgcolor: pickerView === 'minutes' ? 'rgba(108, 92, 231, 0.1)' : '#f8fafc',
                px: 2.5, py: 1.5, borderRadius: 2, cursor: 'pointer',
                border: pickerView === 'minutes' ? '1px solid #6c5ce7' : '1px solid #cbd5e1'
              }}
            >
              <Typography variant="h3" sx={{ color: pickerView === 'minutes' ? '#6c5ce7' : '#1e293b', fontWeight: 500 }}>
                {selectedMinute}
              </Typography>
            </Box>

            <ToggleButtonGroup
              orientation="vertical"
              value={ampm}
              exclusive
              onChange={(e, val) => val && setAmpm(val)}
              sx={{
                border: '1px solid #cbd5e1',
                borderRadius: 2,
                ml: 1,
                bgcolor: '#f8fafc',
                '& .MuiToggleButton-root': {
                  px: 1.5,
                  py: 0.8,
                  fontSize: '0.85rem',
                  border: 'none',
                  color: '#64748b',
                  '&.Mui-selected': { bgcolor: 'rgba(108, 92, 231, 0.1)', color: '#6c5ce7', fontWeight: 'bold' }
                }
              }}
            >
              <ToggleButton value="AM">AM</ToggleButton>
              <ToggleButton value="PM" sx={{ borderTop: '1px solid #cbd5e1 !important' }}>PM</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <Box
              sx={{
                width: 220,
                height: 220,
                borderRadius: '50%',
                bgcolor: '#f8fafc',
                position: 'relative',
                border: '1px solid #cbd5e1'
              }}
            >
              <Box sx={{ width: 6, height: 6, bgcolor: '#6c5ce7', borderRadius: '50%', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 3 }} />
              <Box
                sx={{
                  position: 'absolute',
                  width: 2,
                  height: 76,
                  bgcolor: '#6c5ce7',
                  bottom: '50%',
                  left: '50%',
                  transformOrigin: 'bottom center',
                  transform: `translateX(-50%) rotate(${currentRotation}deg)`,
                  zIndex: 1
                }}
              />

              {currentData.map((item) => {
                const radius = 80;
                const angleRad = (item.deg - 90) * (Math.PI / 180);
                const x = 110 + radius * Math.cos(angleRad);
                const y = 110 + radius * Math.sin(angleRad);
                const isSelected = currentSelection === item.n;

                return (
                  <Box
                    key={item.n}
                    onClick={() => {
                      if (pickerView === 'hours') {
                        setSelectedHour(item.n);
                        setPickerView('minutes');
                      } else {
                        setSelectedMinute(item.n);
                      }
                    }}
                    sx={{
                      position: 'absolute',
                      left: x,
                      top: y,
                      transform: 'translate(-50%, -50%)',
                      fontSize: '0.85rem',
                      color: isSelected ? '#fff' : '#475569',
                      cursor: 'pointer',
                      zIndex: 2,
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      transition: '0.1s',
                      ...(isSelected && { bgcolor: '#6c5ce7', fontWeight: 'bold' }),
                      '&:hover': { bgcolor: isSelected ? '#6c5ce7' : '#e2e8f0' }
                    }}
                  >
                    {parseInt(item.n)}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 0, pb: 0, pt: 2 }}>
          <IconButton size="small" sx={{ color: '#64748b' }}>
            <KeyboardIcon />
          </IconButton>
          <Box>
            <Button onClick={() => setOpenPicker(false)} sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b', mr: 1 }}>
              Cancel
            </Button>
            <Button onClick={handleClockSave} sx={{ textTransform: 'none', fontWeight: 700, color: '#6c5ce7' }}>
              OK
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
}